/**
 * InvoiceRequestModal — /inscrever-escola pós-inscrição.
 * Formulário que envia um pedido de fatura para info@eudactica.com com:
 *   - Escola / Cliente
 *   - Nº de contribuinte (NIF / CIF / VAT)
 *   - Email para envio da fatura
 *   - Observações
 * O admin envia manualmente a fatura ao cliente depois de a emitir.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Send } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  pt: {
    title: "Pedir fatura",
    subtitle: "Preenche os dados fiscais e enviaremos a fatura assim que estiver emitida.",
    school: "Escola / Cliente",
    tax_id: "Nº de contribuinte (NIF)",
    email: "Email para envio",
    notes: "Observações",
    submit: "Enviar pedido",
    cancel: "Cancelar",
    sent: "Pedido enviado. Receberás a fatura no email indicado.",
  },
  en: {
    title: "Request invoice",
    subtitle: "Fill in your tax details and we will send the invoice once it is issued.",
    school: "School / Company",
    tax_id: "Tax ID (VAT / NIF / CIF)",
    email: "Delivery email",
    notes: "Notes",
    submit: "Send request",
    cancel: "Cancel",
    sent: "Request sent. You'll receive the invoice at the email you provided.",
  },
  es: {
    title: "Solicitar factura",
    subtitle: "Rellena tus datos fiscales y te enviaremos la factura cuando esté emitida.",
    school: "Escuela / Empresa",
    tax_id: "Nº de identificación fiscal (CIF/NIF)",
    email: "Email para el envío",
    notes: "Observaciones",
    submit: "Enviar solicitud",
    cancel: "Cancelar",
    sent: "Solicitud enviada. Recibirás la factura en el email indicado.",
  },
};

export default function InvoiceRequestModal({ inscriptionId, defaults, lang = "pt", onClose }) {
  const t = T[lang] || T.pt;
  const [school, setSchool] = useState(defaults?.school || "");
  const [taxId, setTaxId] = useState("");
  const [email, setEmail] = useState(defaults?.email || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!school.trim() || !taxId.trim() || !email.trim()) {
      toast.error(lang === "en" ? "Fill in all required fields" : lang === "es" ? "Rellena todos los campos" : "Preenche todos os campos");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inscriptions/${inscriptionId}/invoice-request?lang=${lang}`, {
        school_name: school.trim(),
        tax_id: taxId.trim(),
        billing_email: email.trim(),
        notes: notes.trim(),
      });
      toast.success(t.sent);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSubmitting(false);
    }
  };

  // Rendered via a portal on `document.body` so it escapes any ancestor
  // stacking context (SiteChrome, sidebar sponsors, sticky header) — the
  // modal always sits on top regardless of where it's mounted.
  return createPortal(
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4 overflow-y-auto" style={{ zIndex: 2147483000 }} data-testid="invoice-request-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-[Fredoka] font-bold text-slate-900">{t.title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{t.subtitle}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100" data-testid="invoice-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label>{t.school}</Label>
            <Input value={school} onChange={(e) => setSchool(e.target.value)} data-testid="invoice-school" />
          </div>
          <div>
            <Label>{t.tax_id}</Label>
            <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} data-testid="invoice-tax-id" />
          </div>
          <div>
            <Label>{t.email}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invoice-email" />
          </div>
          <div>
            <Label>{t.notes}</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full text-sm rounded-md border border-slate-200 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3} data-testid="invoice-notes" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="invoice-cancel">{t.cancel}</Button>
          <Button onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="invoice-submit">
            <Send className="w-4 h-4 mr-1" /> {submitting ? "…" : t.submit}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
